import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { StreamsService } from 'src/app/services/streams.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MetaService } from 'src/app/services/meta.service';
import { DynamicFormComponent } from '../../dynamic-form/dynamic-form.component';
import { TeamsService } from 'src/app/services/teams.service';
import { ConfigService } from 'src/app/services/config.service';
import { HttpClient } from '@angular/common/http';
import { DataService } from 'src/app/services/data.service';
import { StreamEngineService } from 'src/app/services/stream-engine.service';
import { isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { simplifyStatus } from '../../service-status-icon/service-status-icon.component';
import { DialogService } from '../../../services/dialog.service';

interface APIKey {
  key: string;
  issuedTo: string;
  issuedDate: string;
  status: string;
}

@Component({
    selector: 'app-stream-detail-widget',
    templateUrl: './stream-detail-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './stream-detail-widget.component.scss'],
    standalone: false
})
export class StreamDetailWidgetComponent extends BaseWidgetComponent implements OnInit, AfterViewInit {
  private dialogs = inject(DialogService);
  @ViewChild(MatPaginator) keysPaginator: MatPaginator;
  @ViewChild(MatSort, { static: false }) keysSort: MatSort;
  @ViewChild(DynamicFormComponent) settingsForm: DynamicFormComponent;

  @Input() streamName: string;
  @Output() closeClick = new EventEmitter<void>();

  public title: string = 'Stream';
  public isLoading = true;
  public isLoadingSettings = true;
  public selectedStreamName: string;
  public stream: any = null;
  public streamParameters;
  public isNew = false;
  public meta: any = null;
  public availableAdapters: string[] = [];
  public selectedAdapter: string;
  public serviceData: any = { name: null, host: null, status: null };
  public statusClass: string = 'unknown';
  public command: string = null;
  public terminalId = new Date().getTime().toString();
  public isRestAdapter: boolean;
  public apiKeys = new MatTableDataSource<APIKey>([]);
  public apiKeysColumns = ['key', 'issuedTo', 'issuedDate', 'status', 'action'];
  public apiKeyStatuses = ['enabled', 'revoked'];
  public isSaving = false;
  public isDirty = false;
  public isUpdatingService = true;

  constructor(
    private _streamsService: StreamsService,
    private _metaService: MetaService,
    private _teamsService: TeamsService,
    private _dataService: DataService,
    private _http: HttpClient,
    private _streamEngineService: StreamEngineService,
    public snackbar: MatSnackBar
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.selectedStreamName = this.streamName;

    if (this.streamName === 'add') {
      this.title = 'New Stream';
      this.isNew = true;
      this.isDirty = true;
      this.stream = { adapter: { name: null }, parameters: {} };
      this.isLoading = false;
      this.isLoadingSettings = false;
      await this.loadAdapters();
    } else {
      this.title = `Stream: ${this.streamName}`;
      await this.loadAdapters();
      this.loadStreamDetail();
    }
  }

  ngAfterViewInit(): void {
    this._initKeysTable();
  }

  private _initKeysTable(data?: APIKey[]) {
    if (data !== undefined) this.apiKeys = new MatTableDataSource(data);

    this.apiKeys.sort = this.keysSort;
    this.apiKeys.paginator = this.keysPaginator;
  }

  async loadAdapters() {
    this.availableAdapters = (await this._dataService.loadAdaptersAsync(this.teamName)).map((x) => x.name);
  }

  loadStreamDetail() {
    this.isLoadingSettings = true;

    const name = `lit-stream@${this.teamName}:${this.selectedStreamName}`;
    this.command = `sudo journalctl -f -u ${name}`;

    this.loadServiceData();

    const subsub = this._streamEngineService.loadOne(this.teamName, this.selectedStreamName).subscribe((response) => {
      subsub.unsubscribe();
      if (response.stream) this.stream = response.stream;
    });

    const sub = this._streamsService.get(this.teamName, this.selectedStreamName).subscribe(async (response: any) => {
      sub.unsubscribe();
      this.stream = response;
      this.selectedAdapter = response.adapter?.name || response.adapter;
      this.isRestAdapter = response.ui === 'rest';

      const meta: any = await this._metaService.getAsync('adapter', this.teamName, this.selectedAdapter);
      for (let param of meta.params) param.description = null;

      if ('api_keys' in this.stream.parameters) {
        this._initKeysTable(this.stream.parameters['api_keys'].map((key) => ({ issuedTo: null, issuedDate: null, ...key })));
      } else {
        this._initKeysTable([]);
      }

      this.streamParameters = this.stream.parameters;
      this.meta = meta;
      this.isLoadingSettings = false;
      this.isLoading = false;
    });
  }

  loadServiceData() {
    const sub = this._streamEngineService.loadWorkers(this.teamName).subscribe((response) => {
      sub.unsubscribe();
      const worker = (response || []).find((x) => x.name === this.selectedStreamName);
      if (worker) {
        this.serviceData = { ...this.serviceData, ...worker };
        this.statusClass = simplifyStatus(worker);
      }
      this.isUpdatingService = false;
    });
  }

  markDirty() {
    this.isDirty = true;
  }

  onFormLoaded(formRef?: DynamicFormComponent) {
    const form = formRef?.form || this.settingsForm?.form;
    if (form) {
      // Skip the initial valueChanges emission from form creation
      let initialized = false;
      setTimeout(() => initialized = true);
      form.valueChanges.subscribe(() => {
        if (initialized) this.markDirty();
      });
    }
  }

  isDeployValid() {
    return !isNil(this.selectedStreamName) && !isNil(this.stream);
  }

  async onStart() {
    this.isUpdatingService = true;
    this._streamEngineService.start(this.teamName, this.selectedStreamName).subscribe((response) => {
      this.loadServiceData();
    });
  }

  async onStop() {
    this.isUpdatingService = true;
    this._streamEngineService.stop(this.teamName, this.selectedStreamName).subscribe((response) => {
      this.loadServiceData();
    });
  }

  async onRestart() {
    this.isUpdatingService = true;
    this._streamEngineService.restart(this.teamName, this.selectedStreamName).subscribe((response) => {
      this.loadServiceData();
    });
  }

  async delete() {
    if (!await this.dialogs.confirm(`Are you sure you want to delete ${this.selectedStreamName}?`)) return;
    await this._streamsService.deleteAsync(this.teamName, this.selectedStreamName);
    this.close();
  }

  private async _save() {
    this.isSaving = true;

    const teamConfig = await this._teamsService.getConfigAsync(this.teamName);
    const path = `${teamConfig.path.streams}/${this.selectedStreamName.replace(' ', '_')}.json`;
    await this.postFile({ path: path, type: 'json', data: this.stream }, this.isRestAdapter && this.isNew);

    this.isSaving = false;
    this.isDirty = false;
  }

  async save() {
    const params = this.settingsForm.getFormData();

    this.stream.adapter.name = this.selectedAdapter;
    Object.keys(params).forEach((key) => {
      if (key !== 'id') this.stream.parameters[key] = params[key];
    });
    if (!this.isValid()) return;

    this._save();
  }

  async saveKeys() {
    if (!this.isRestAdapter) return;

    this.stream.adapter.name = this.selectedAdapter;
    this.stream.parameters['api_keys'] = this.apiKeys.data;
    if (!this.isValid()) return;

    await this._save();
  }

  async postFile(data: any, createSwagger: boolean = false): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const sub = this._http.post(`${ConfigService.apiUrl}/file/${this.teamName}`, data).subscribe({
        error: (error) => {
          sub.unsubscribe();
          this.openSnackBar('Error saving stream.', 'OK');
          console.error(error);
          reject(error);
        },
        complete: () => {
          sub.unsubscribe();
          if (createSwagger) {
            this.initSwagger(this.selectedStreamName.replace(' ', '_'));
          } else {
            this.isNew = false;
            this.openSnackBar('Save successful. Restart required.', 'OK');
          }
          resolve();
        }
      });
    });
  }

  initSwagger(streamName: string) {
    const sub = this._streamsService.createSwagger(this.teamName, streamName).subscribe((response) => {
      sub.unsubscribe();
      this.isNew = false;
      this.openSnackBar('Save successful.', 'OK');
    });
  }

  async onAdapterSelected($event) {
    this.isLoadingSettings = true;
    const meta: any = await this._metaService.getAsync('adapter', this.teamName, this.selectedAdapter);
    for (let param of meta.params) {
      param.description = null;
    }
    this.meta = meta;
    const params = {};

    for (let item of this.meta.params) {
      params[item.name] = item.type === 'boolean' ? false : null;
      if (item.name === 'port') this.isRestAdapter = true;
    }

    this.streamParameters = params;
    this.isLoadingSettings = false;
  }

  isValid() {
    let valid = true;
    let message = '';

    if (!this.selectedStreamName || this.selectedStreamName.length < 4) {
      valid = false;
      message = 'Stream name must be at least 3 characters long.\n';
    }

    for (let item of this.meta.params) {
      if (item.type === 'boolean' || item.required === false) continue;

      if (!this.stream.parameters[item.name]) {
        valid = false;
        message = `${message} ${item.name} is required.\n`;
        continue;
      }

      if (item.type === 'number') {
        if (this.stream.parameters[item.name] === null && item.required === false) continue;

        if (isNaN(this.stream.parameters[item.name])) {
          valid = false;
          message = `${message} ${item.name} must be numeric.\n`;
        }
      }
    }

    if (!valid) {
      this.dialogs.alert(message);
    }
    return valid;
  }

  getCurrentTimestamp() {
    const now = new Date();
    const options: any = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short'
    };
    return now.toLocaleString('en-US', options);
  }

  addKey(): void {
    const timestamp = this.getCurrentTimestamp();
    const keys = this.apiKeys.data;
    keys.unshift({ key: uuidv4(), status: 'enabled', issuedTo: null, issuedDate: timestamp });
    this._initKeysTable(keys);
  }

  async deleteKey(row: APIKey): Promise<void> {
    if (!await this.dialogs.confirm('Are you sure you want to delete this key?')) return;
    const keys = this.apiKeys.data.filter((x) => x.key !== row.key);
    this._initKeysTable(keys);
  }

  copyKey(row: APIKey): void {
    if (navigator.clipboard !== undefined) {
      navigator.clipboard.writeText(row.key);
      this.openSnackBar('Key copied to clipboard.', 'OK');
    } else console.info('Navigator does not have clipboard (requires HTTPS)');
  }

  $inputIssuedTo($ev: Event, apiKey: APIKey) {
    apiKey.issuedTo = (<HTMLInputElement>$ev.target).value;
  }

  applyKeysFilter(filterValue: string) {
    this.apiKeys.filter = filterValue.trim().toLowerCase();
    if (this.apiKeys.paginator) this.apiKeys.paginator.firstPage();
  }

  openSnackBar(message: string, action: string) {
    const durationInSeconds = 3;
    const horizontalPosition: MatSnackBarHorizontalPosition = 'center';
    const verticalPosition: MatSnackBarVerticalPosition = 'top';

    this.snackbar.open(message, action, {
      horizontalPosition,
      verticalPosition,
      duration: durationInSeconds * 1000
    });
  }
}
